import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useContractCopyMutation } from "../../services/server/ContractApi";
import type { ContractCopyEntity } from "../../types/ContractCopyEntity";
import contractCopyJson from "./contract-copy.json";

export const ContractCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = contractCopyJson as FormJson;

    const [formData, setFormData] = useState<ContractCopyEntity>({} as ContractCopyEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractCopyMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                const { data } = result;
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: data.id,
                            resource: "Contract",
                        }),
                        autoCloseDisabled: false,
                    }),
                );

                const route = data.physical
                    ? `${BASE_PATH}/${ResourceName.CONTRACTS}/${ResourceVariant.PHYSICAL}/${FORM_ACTION.EDIT}/${data.id}?discriminator=PHYSICAL&service=true&id=${data.id}`
                    : `${BASE_PATH}/${ResourceName.CONTRACTS}/${ResourceVariant.VIRTUAL}/${FORM_ACTION.EDIT}/${data.id}?discriminator=VIRTUAL&service=true&id=${data.id}`;
                navigate(route);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    const { id } = useParams();

    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<ContractCopyEntity>
                    name="ContractCopyForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
